import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Plus, ClipboardCheck, Eye, Trash2, CheckCircle, XCircle, Clock,
  ChevronDown, ChevronRight, FolderOpen, Folder, Archive, Zap,
  Search, RefreshCw
} from 'lucide-react';
import api from '../../api.js';
import { useActiveVehicles, useActiveDrivers } from '../../hooks/useDataHooks.js';
import { useAccordionState } from '../../hooks/useAccordionState.js';
import InspectionFormModal from './InspectionFormModal.jsx';
import InspectionDetailModal from './InspectionDetailModal.jsx';
import { useSearch } from '../../v3/context/SearchContext.jsx';
import { useFormat } from '../../i18n/format.js';

// Loading Spinner
function LoadingSpinner() {
  const { t } = useFormat();
  return (
    <div className="flex justify-center items-center h-64">
      <div className="text-gray-600">{t('common.states.loading')}</div>
    </div>
  );
}

// Status configuration (libellé résolu au rendu via t('inspections.status.<code>'))
const STATUS_CONFIG = {
  draft: { color: 'bg-gray-100 text-gray-800', icon: Clock, group: 'current' },
  submitted: { color: 'bg-blue-100 text-blue-800', icon: ClipboardCheck, group: 'current' },
  passed: { color: 'bg-green-100 text-green-800', icon: CheckCircle, group: 'done' },
  failed: { color: 'bg-red-100 text-red-800', icon: XCircle, group: 'done' }
};

// Status order for display
const STATUS_ORDER = {
  current: ['draft', 'submitted'],
  done: ['passed', 'failed']
};

// Type colors (libellé résolu au rendu via t('inspections.types.<code>'))
const TYPE_CONFIG = {
  pre_trip: { color: 'bg-indigo-100 text-indigo-800' },
  post_trip: { color: 'bg-purple-100 text-purple-800' },
  daily: { color: 'bg-cyan-100 text-cyan-800' },
  weekly: { color: 'bg-teal-100 text-teal-800' },
  dvir: { color: 'bg-amber-100 text-amber-800' },
  special: { color: 'bg-pink-100 text-pink-800' }
};

function InspectionsView({ onMessage, createForVehicleId, returnTo, onNavigate }) {
  const { t, fmt } = useFormat();
  const [inspLoading, setInspLoading] = useState(true);
  const [inspections, setInspections] = useState([]);
  const { data: vehicles = [], isLoading: vehiclesLoading } = useActiveVehicles();
  const { data: drivers = [], isLoading: driversLoading } = useActiveDrivers();
  const [showForm, setShowForm] = useState(false);
  const [selectedInspection, setSelectedInspection] = useState(null);
  const { searchQuery } = useSearch();
  const createForVehicleProcessed = useRef(false);

  // Accordion state - persisted to DB
  const {
    isExpanded,
    toggleGroup: toggleAccordion,
  } = useAccordionState({ viewCode: 'inspections' });

  // Toggle wrappers with prefixed keys
  const toggleGroup = (group) => toggleAccordion(`group:${group}`);
  const toggleStatus = (status) => toggleAccordion(`status:${status}`);

  const loading = inspLoading || vehiclesLoading || driversLoading;

  const loadData = async () => {
    try {
      setInspLoading(true);
      const inspRes = await api.inspection.getAll();
      setInspections(inspRes.data || []);
    } catch (error) {
      console.error('Error loading inspections:', error);
      onMessage?.({
        title: t('inspections.messages.errorTitle'),
        message: t('inspections.errors.loadFailed'),
        type: 'error'
      });
    } finally {
      setInspLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-open creation form for a specific vehicle (from Vehicles action menu)
  useEffect(() => {
    if (createForVehicleId && !createForVehicleProcessed.current) {
      createForVehicleProcessed.current = true;
      setShowForm(true);
    }
  }, [createForVehicleId]);

  // Filter inspections by search
  const filteredInspections = useMemo(() => {
    if (!searchQuery || searchQuery.length < 2) return inspections;
    const query = searchQuery.toLowerCase();
    return inspections.filter(insp => {
      const vehicle = vehicles.find(v => v.id === insp.vehicle_id);
      const driver = drivers.find(d => d.id === insp.driver_id);
      return (
        insp.inspection_date?.toLowerCase().includes(query) ||
        insp.type?.toLowerCase().includes(query) ||
        insp.status?.toLowerCase().includes(query) ||
        vehicle?.license_plate?.toLowerCase().includes(query) ||
        vehicle?.make?.toLowerCase().includes(query) ||
        vehicle?.model?.toLowerCase().includes(query) ||
        driver?.first_name?.toLowerCase().includes(query) ||
        driver?.last_name?.toLowerCase().includes(query)
      );
    });
  }, [inspections, vehicles, drivers, searchQuery]);

  // Group inspections by current/done and then by status
  const groupedInspections = useMemo(() => {
    const groups = {
      current: {
        label: t('inspections.groups.current'),
        icon: Zap,
        statuses: {},
        totalCount: 0
      },
      done: {
        label: t('inspections.groups.done'),
        icon: Archive,
        statuses: {},
        totalCount: 0
      }
    };

    // Initialize status groups
    STATUS_ORDER.current.forEach(status => {
      groups.current.statuses[status] = [];
    });
    STATUS_ORDER.done.forEach(status => {
      groups.done.statuses[status] = [];
    });

    // Sort inspections into groups
    filteredInspections.forEach(inspection => {
      const statusConfig = STATUS_CONFIG[inspection.status];
      if (statusConfig) {
        const groupKey = statusConfig.group;
        groups[groupKey].statuses[inspection.status].push(inspection);
        groups[groupKey].totalCount++;
      }
    });

    return groups;
  }, [filteredInspections, t]);

  // Stats
  const stats = useMemo(() => ({
    total: inspections.length,
    current: groupedInspections.current.totalCount,
    passed: (groupedInspections.done.statuses.passed || []).length,
    failed: (groupedInspections.done.statuses.failed || []).length
  }), [inspections, groupedInspections]);

  const handleFormSuccess = () => {
    loadData();
    setShowForm(false);
    // Return to origin view (e.g. vehicles) after successful submission
    if (returnTo && onNavigate) {
      onNavigate(returnTo);
    }
  };

  const handleDelete = async (inspectionId) => {
    if (!window.confirm(t('inspections.messages.deleteConfirm'))) return;

    try {
      await api.inspection.delete(inspectionId);
      onMessage?.({
        title: t('inspections.messages.successTitle'),
        message: t('inspections.messages.deleted'),
        type: 'success'
      });
      loadData();
    } catch (error) {
      onMessage?.({
        title: t('inspections.messages.errorTitle'),
        message: t('inspections.errors.deleteFailed'),
        type: 'error'
      });
    }
  };

  // Get vehicle info
  const getVehicleInfo = (vehicleId) => {
    const vehicle = vehicles.find(v => v.id === vehicleId);
    return vehicle
      ? `${vehicle.make} ${vehicle.model} - ${vehicle.license_plate}`
      : t('inspections.list.vehicleFallback', { id: vehicleId });
  };

  // Get driver info
  const getDriverInfo = (driverId) => {
    if (!driverId) return '-';
    const driver = drivers.find(d => d.id === driverId);
    return driver
      ? `${driver.first_name} ${driver.last_name}`
      : t('inspections.list.driverFallback', { id: driverId });
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-4">
      {/* Form Modal */}
      <InspectionFormModal
        isOpen={showForm}
        onClose={() => setShowForm(false)}
        onSuccess={handleFormSuccess}
        onMessage={onMessage}
        vehicles={vehicles}
        drivers={drivers}
        defaultVehicleId={createForVehicleId}
      />

      {/* Detail Modal */}
      {selectedInspection && (
        <InspectionDetailModal
          inspection={selectedInspection}
          onClose={() => setSelectedInspection(null)}
          onUpdate={loadData}
          onMessage={onMessage}
          vehicles={vehicles}
          drivers={drivers}
        />
      )}

      {/* Header Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex justify-between items-start mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <ClipboardCheck className="w-6 h-6 text-indigo-600" />
              {t('inspections.list.title')}
            </h2>
            <p className="text-gray-600 mt-1 flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-gray-900">
                {t('inspections.list.inspectionCount', { count: stats.total })}
              </span>
              <span className="text-gray-300">|</span>
              <span className="text-blue-600">{t('inspections.list.pendingCount', { count: stats.current })}</span>
              <span className="text-gray-300">|</span>
              <span className="text-green-600">{t('inspections.list.passedCount', { count: stats.passed })}</span>
              {stats.failed > 0 && (
                <>
                  <span className="text-gray-300">|</span>
                  <span className="text-red-600">{t('inspections.list.failedCount', { count: stats.failed })}</span>
                </>
              )}
            </p>
          </div>
          <button
            onClick={() => setShowForm(true)}
            className="px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            {t('inspections.list.newInspection')}
          </button>
        </div>

        {/* Refresh */}
        <div className="flex gap-4 pt-4 border-t border-gray-200 justify-end">
          <button
            onClick={loadData}
            disabled={loading}
            className="px-3 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg border border-gray-300 transition-colors"
            title={t('common.actions.refresh')}
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Accordion Groups */}
      <div className="space-y-3">
        {['current', 'done'].map((groupKey) => {
          const group = groupedInspections[groupKey];
          const isGroupExpanded = isExpanded(`group:${groupKey}`);
          const GroupIcon = group.icon;
          const statusList = STATUS_ORDER[groupKey];
          const nonEmptyStatuses = statusList.filter(s => group.statuses[s].length > 0);

          return (
            <div
              key={groupKey}
              className={`border rounded-lg overflow-hidden transition-all ${
                isGroupExpanded ? 'border-blue-300 shadow-sm' : 'border-gray-200'
              }`}
            >
              {/* Group Header */}
              <button
                onClick={() => toggleGroup(groupKey)}
                className={`w-full flex items-center justify-between p-4 text-left transition-colors ${
                  isGroupExpanded
                    ? 'bg-blue-50 hover:bg-blue-100'
                    : 'bg-white hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center gap-3">
                  {isGroupExpanded ? (
                    <FolderOpen className="w-5 h-5 text-blue-600" />
                  ) : (
                    <Folder className="w-5 h-5 text-gray-400" />
                  )}
                  <GroupIcon className={`w-5 h-5 ${
                    groupKey === 'current' ? 'text-blue-500' : 'text-green-500'
                  }`} />
                  <span className={`text-lg font-semibold ${
                    isGroupExpanded ? 'text-blue-900' : 'text-gray-900'
                  }`}>
                    {group.label}
                  </span>
                  {groupKey === 'current' && group.totalCount > 0 && (
                    <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded-full animate-pulse">
                      {t('inspections.list.toProcess')}
                    </span>
                  )}
                </div>

                <div className="flex items-center gap-4">
                  {!isGroupExpanded && (
                    <span className="text-sm text-gray-500">
                      {t('inspections.list.inspectionCount', { count: group.totalCount })} • {t('inspections.list.statusCount', { count: nonEmptyStatuses.length })}
                    </span>
                  )}
                  <span className={`px-2.5 py-1 text-sm font-medium rounded-full ${
                    isGroupExpanded ? 'bg-blue-200 text-blue-800' : 'bg-gray-100 text-gray-600'
                  }`}>
                    {group.totalCount}
                  </span>
                  {isGroupExpanded ? (
                    <ChevronDown className="w-5 h-5 text-blue-600" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  )}
                </div>
              </button>

              {/* Group Content - Status Accordions */}
              {isGroupExpanded && (
                <div className="border-t border-blue-200 bg-gray-50">
                  <div className="divide-y divide-gray-200">
                    {statusList.map((status) => {
                      const statusInspections = group.statuses[status];
                      const statusConfig = STATUS_CONFIG[status];
                      const StatusIcon = statusConfig.icon;
                      const isStatusExpanded = isExpanded(`status:${status}`);

                      if (statusInspections.length === 0) return null;

                      return (
                        <div key={status} className="bg-white">
                          {/* Status Header */}
                          <button
                            onClick={() => toggleStatus(status)}
                            className={`w-full flex items-center justify-between px-4 py-3 text-left transition-colors ${
                              isStatusExpanded
                                ? 'bg-gray-50 border-l-4 border-blue-500'
                                : 'hover:bg-gray-50 border-l-4 border-transparent'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              {isStatusExpanded ? (
                                <ChevronDown className="w-4 h-4 text-blue-600" />
                              ) : (
                                <ChevronRight className="w-4 h-4 text-gray-400" />
                              )}
                              <StatusIcon className={`w-4 h-4 ${
                                status === 'passed' ? 'text-green-500' :
                                status === 'failed' ? 'text-red-500' :
                                status === 'submitted' ? 'text-blue-500' :
                                'text-gray-500'
                              }`} />
                              <span className={`font-medium ${
                                isStatusExpanded ? 'text-blue-900' : 'text-gray-700'
                              }`}>
                                {t(`inspections.status.${status}`)}
                              </span>
                            </div>

                            <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${statusConfig.color}`}>
                              {t('inspections.list.inspectionCount', { count: statusInspections.length })}
                            </span>
                          </button>

                          {/* Status Content - Inspection List */}
                          {isStatusExpanded && (
                            <div className="px-4 py-3 space-y-2 bg-gray-50 border-l-4 border-blue-500">
                              {statusInspections.map(inspection => {
                                const typeConfig = TYPE_CONFIG[inspection.type] || { color: 'bg-gray-100 text-gray-800' };

                                return (
                                  <div
                                    key={inspection.id}
                                    className="bg-white border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors"
                                  >
                                    <div className="flex items-start justify-between">
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-1">
                                          <h4 className="font-medium text-gray-900">
                                            {inspection.inspection_date}
                                          </h4>
                                          <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${typeConfig.color}`}>
                                            {t(`inspections.types.${inspection.type}`, { defaultValue: inspection.type })}
                                          </span>
                                          {inspection.issues_found > 0 && (
                                            <span className="px-2 py-0.5 text-xs rounded-full font-medium bg-red-100 text-red-700">
                                              {t('inspections.list.issuesCount', { count: inspection.issues_found })}
                                            </span>
                                          )}
                                        </div>
                                        <div className="text-sm text-gray-600 mb-1">
                                          {getVehicleInfo(inspection.vehicle_id)}
                                        </div>
                                        <div className="flex items-center gap-4 text-xs text-gray-500">
                                          <span>{t('inspections.list.driver', { name: getDriverInfo(inspection.driver_id) })}</span>
                                          {inspection.odometer && (
                                            <span>{fmt.number(inspection.odometer)} km</span>
                                          )}
                                        </div>
                                      </div>
                                      <div className="flex items-center gap-2 ml-4">
                                        <button
                                          onClick={() => setSelectedInspection(inspection)}
                                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                                          title={t('inspections.list.viewDetails')}
                                        >
                                          <Eye className="w-4 h-4" />
                                        </button>
                                        <button
                                          onClick={() => handleDelete(inspection.id)}
                                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                                          title={t('common.actions.delete')}
                                        >
                                          <Trash2 className="w-4 h-4" />
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}

                    {/* Empty state for group */}
                    {nonEmptyStatuses.length === 0 && (
                      <div className="py-8 text-center text-gray-500">
                        <ClipboardCheck className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                        <p>{groupKey === 'current' ? t('inspections.list.emptyGroupPending') : t('inspections.list.emptyGroupDone')}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Global Empty State */}
      {inspections.length === 0 && (
        <div className="bg-white p-12 rounded-lg shadow-md text-center">
          <ClipboardCheck className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-900 mb-2">{t('inspections.list.emptyTitle')}</h3>
          <p className="text-gray-600 mb-4">
            {t('inspections.list.emptyHint')}
          </p>
          <button
            onClick={() => setShowForm(true)}
            className="px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 inline-flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            {t('inspections.list.newInspection')}
          </button>
        </div>
      )}
    </div>
  );
}

export default InspectionsView;
